import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { ApiClient } from '../utils/apiClient';

const WebsiteURLManager = ({ urls = [], onChange, disabled = false, parrotId = null, stagedMode = false }) => {
  const { getToken } = useAuth();
  const { showError, showSuccess } = useToast();
  const { theme } = useTheme();
  const [newUrl, setNewUrl] = useState('');
  const [validatingUrls, setValidatingUrls] = useState(new Set());
  const [urlStatuses, setUrlStatuses] = useState(new Map());
  const [loading, setLoading] = useState(false);

  // Initialize API client
  const api = new ApiClient(getToken);

  // Load URL statuses when parrotId is available
  useEffect(() => {
    if (parrotId) {
      loadUrlStatuses();
    }
  }, [parrotId]);

  // Load URL sync statuses from the API
  const loadUrlStatuses = async () => {
    if (!parrotId) return;

    try {
      setLoading(true);
      const response = await api.get(`/api/parrots/${parrotId}/urls`);

      if (response.success) {
        const statusMap = new Map();
        const loadedUrls = [];
        const seenUrls = new Set();

        // Remove duplicates and track unique URLs
        response.urls.forEach(urlRecord => {
          if (!seenUrls.has(urlRecord.url)) {
            seenUrls.add(urlRecord.url);
            statusMap.set(urlRecord.url, {
              id: urlRecord.id,
              sync_status: urlRecord.sync_status || 'unknown',
              sync_error_message: urlRecord.sync_error_message,
              last_crawled: urlRecord.last_crawled,
              crawl_error: urlRecord.crawl_error
            });
            loadedUrls.push(urlRecord.url);
          }
        });

        setUrlStatuses(statusMap);

        // Always update parent component with loaded URLs (backend cleanup may have changed them)
        if (onChange) {
          onChange(loadedUrls);
        }
      }
    } catch (error) {
      console.error('Failed to load URL statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  // Validate URL format
  const isValidUrl = (urlString) => {
    try {
      const url = new URL(urlString);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  // Validate URL using the API
  const validateUrlWithAPI = async (url) => {
    if (!parrotId) {
      // Fallback to basic validation if no parrotId
      return { valid: isValidUrl(url), accessible: true };
    }

    try {
      setValidatingUrls(prev => new Set([...prev, url]));

      const response = await api.post(`/api/parrots/${parrotId}/urls/validate`, { url });

      return {
        valid: response.valid,
        accessible: response.valid, // Backend considers valid URLs as accessible
        normalized_url: response.url || url, // Backend returns 'url', not 'normalized_url'
        error: response.error
      };
    } catch (error) {
      console.warn(`URL validation failed for ${url}:`, error);
      return { valid: false, accessible: false, error: error.message };
    } finally {
      setValidatingUrls(prev => {
        const newSet = new Set(prev);
        newSet.delete(url);
        return newSet;
      });
    }
  };

  const handleAddUrl = async () => {
    const trimmedUrl = newUrl.trim();

    if (!trimmedUrl) {
      showError('Please enter a URL');
      return;
    }

    if (!isValidUrl(trimmedUrl)) {
      showError('Please enter a valid URL (must start with http:// or https://)');
      return;
    }

    if (urls.includes(trimmedUrl)) {
      showError('This URL is already added');
      return;
    }

    // ElevenLabs Agents only support 1 URL
    if (urls.length >= 1) {
      showError('Only one website URL is allowed. Please remove the existing URL first.');
      return;
    }

    if (stagedMode) {
      // Staged mode: Just add to local state, will be saved later
      const updatedUrls = [...urls, trimmedUrl];
      onChange(updatedUrls);
      setNewUrl('');
      showSuccess('Website URL added to configuration (will be saved when you complete this step)');
    } else {
      // Immediate mode: Validate and save to database immediately
      if (parrotId) {
        const validation = await validateUrlWithAPI(trimmedUrl);

        if (!validation.valid) {
          showError(validation.error || 'Invalid URL format');
          return;
        }

        try {
          // Add URL via API
          const response = await api.post(`/api/parrots/${parrotId}/urls`, {
            url: validation.normalized_url,
            title: null
          });

          if (response.success) {
            // Update local state
            const updatedUrls = [...urls, validation.normalized_url];
            onChange(updatedUrls);

            // Update status map
            setUrlStatuses(prev => new Map(prev).set(validation.normalized_url, {
              id: response.id,
              sync_status: 'queued',
              sync_error_message: null,
              last_crawled: null,
              crawl_error: null
            }));

            setNewUrl('');
            showSuccess('Website URL added successfully and queued for crawling');

            // Refresh URL list to ensure state is synchronized
            await loadUrlStatuses();
          }
        } catch (error) {
          showError(error.message || 'Failed to add URL');

          // Refresh URL list even on error to ensure state is synchronized
          await loadUrlStatuses();
        }
      } else {
        // Fallback for when no parrotId (legacy mode)
        const updatedUrls = [...urls, trimmedUrl];
        onChange(updatedUrls);
        setNewUrl('');
        showSuccess('Website URL added successfully');
      }
    }
  };

  const handleRemoveUrl = async (urlToRemove) => {
    if (stagedMode) {
      // Staged mode: Just remove from local state, will be saved later
      const updatedUrls = urls.filter(url => url !== urlToRemove);
      onChange(updatedUrls);
      showSuccess('Website URL removed from configuration');
    } else {
      // Immediate mode: Remove from database immediately
      if (parrotId) {
        const urlStatus = urlStatuses.get(urlToRemove);

        if (urlStatus?.id) {
          try {
            await api.delete(`/api/parrots/${parrotId}/urls/${urlStatus.id}`);

            // Update local state
            const updatedUrls = urls.filter(url => url !== urlToRemove);
            onChange(updatedUrls);

            // Remove from status map
            setUrlStatuses(prev => {
              const newMap = new Map(prev);
              newMap.delete(urlToRemove);
              return newMap;
            });

            showSuccess('Website URL removed successfully');
          } catch (error) {
            showError(error.message || 'Failed to remove URL');
          }
        } else {
          // Fallback for URLs without status
          const updatedUrls = urls.filter(url => url !== urlToRemove);
          onChange(updatedUrls);
          showSuccess('Website URL removed');
        }
      } else {
        // Fallback for when no parrotId (legacy mode)
        const updatedUrls = urls.filter(url => url !== urlToRemove);
        onChange(updatedUrls);
        showSuccess('Website URL removed');
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddUrl();
    }
  };

  // Get sync status indicator for a URL
  const getSyncStatusIndicator = (url) => {
    const status = urlStatuses.get(url);

    if (!status) {
      return {
        icon: '🌐',
        text: 'Ready',
        color: 'text-gray-400',
        bgColor: 'bg-gray-600'
      };
    }

    switch (status.sync_status) {
      case 'queued':
        return {
          icon: '⏳',
          text: 'Queued for crawling',
          color: 'text-yellow-400',
          bgColor: 'bg-yellow-600'
        };
      case 'processing':
        return {
          icon: '🔄',
          text: 'Crawling in progress',
          color: 'text-blue-400',
          bgColor: 'bg-blue-600'
        };
      case 'completed':
        return {
          icon: '✅',
          text: status.last_crawled ? `Crawled ${new Date(status.last_crawled).toLocaleDateString()}` : 'Crawled successfully',
          color: 'text-green-400',
          bgColor: 'bg-green-600'
        };
      case 'failed':
        return {
          icon: '❌',
          text: status.sync_error_message || status.crawl_error || 'Crawling failed',
          color: 'text-red-400',
          bgColor: 'bg-red-600'
        };
      default:
        return {
          icon: '❓',
          text: 'Unknown status',
          color: 'text-gray-400',
          bgColor: 'bg-gray-600'
        };
    }
  };

  // Retry sync for a failed URL
  const handleRetrySync = async (url) => {
    const urlStatus = urlStatuses.get(url);

    if (!urlStatus?.id || !parrotId) return;

    try {
      await api.post(`/api/parrots/${parrotId}/urls/${urlStatus.id}/retry-sync`);

      // Update status to queued
      setUrlStatuses(prev => new Map(prev).set(url, {
        ...urlStatus,
        sync_status: 'queued',
        sync_error_message: null,
        crawl_error: null
      }));

      showSuccess('URL sync retry queued');
    } catch (error) {
      showError(error.message || 'Failed to retry URL sync');
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
          Website Knowledge Sources
        </label>
        <p className={`text-xs mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
          Add a website URL that your parrot can reference for knowledge extraction.
          The site will be crawled to provide up-to-date information to your AI parrot.
        </p>

        {/* Add new URL input */}
        {urls.length === 0 ? (
          <div className="flex gap-2 mb-4">
            <input
              type="url"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="https://example.com/knowledge-base"
              disabled={disabled}
              className={`flex-1 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50 ${theme === 'dark'
                ? 'bg-gray-700 border border-gray-600 text-white'
                : 'bg-white border border-gray-300 text-gray-900'
                }`}
            />
            <button
              type="button"
              onClick={handleAddUrl}
              disabled={disabled || !newUrl.trim()}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:text-white"
            >
              Add URL
            </button>
          </div>
        ) : (
          <div className={`p-3 rounded-lg mb-4 ${theme === 'dark'
            ? 'bg-blue-900/20 border border-blue-700/50'
            : 'bg-blue-50 border border-blue-200'
            }`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-blue-300' : 'text-blue-700'
              }`}>
              <strong>Note:</strong> Only one website URL is supported. To add a different URL, remove the existing one first.
            </p>
          </div>
        )}
      </div>

      {/* Display existing URLs */}
      {urls.length > 0 && (
        <div>
          <h5 className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
            Website Source ({urls.length}/1)
          </h5>
          <div className="space-y-2">
            {urls.map((url, index) => {
              const statusInfo = getSyncStatusIndicator(url);
              const isValidating = validatingUrls.has(url);
              const canRetry = statusInfo.text.includes('failed') && parrotId;

              return (
                <div
                  key={index}
                  className={`flex items-center justify-between p-3 rounded-lg border ${theme === 'dark'
                    ? 'bg-gray-700 border-gray-600'
                    : 'bg-gray-50 border-gray-200'
                    }`}
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="flex-shrink-0">
                      <svg className="w-4 h-4 text-teal-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.083 9h1.946c.089-1.546.383-2.97.837-4.118A6.004 6.004 0 004.083 9zM10 2a8 8 0 100 16 8 8 0 000-16zm0 2c-.076 0-.232.032-.465.262-.238.234-.497.623-.737 1.182-.389.907-.673 2.142-.766 3.556h3.936c-.093-1.414-.377-2.649-.766-3.556-.24-.559-.499-.948-.737-1.182C10.232 4.032 10.076 4 10 4zm3.971 5c-.089-1.546-.383-2.97-.837-4.118A6.004 6.004 0 0115.917 9h-1.946zm-2.003 2H8.032c.093 1.414.377 2.649.766 3.556.24.559.499.948.737 1.182.233.23.389.262.465.262.076 0 .232-.032.465-.262.238-.234.497-.623.737-1.182.389-.907.673-2.142.766-3.556zm1.166 4.118c.454-1.147.748-2.572.837-4.118h1.946a6.004 6.004 0 01-2.783 4.118zm-6.268 0C6.412 13.97 6.118 12.546 6.03 11H4.083a6.004 6.004 0 002.783 4.118z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`} title={url}>
                        {url}
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`text-xs ${statusInfo.color}`}>
                          {statusInfo.icon} {statusInfo.text}
                        </span>
                        {canRetry && (
                          <button
                            type="button"
                            onClick={() => handleRetrySync(url)}
                            className={`text-xs underline ${theme === 'dark'
                              ? 'text-blue-400 hover:text-blue-300'
                              : 'text-blue-600 hover:text-blue-500'
                              }`}
                          >
                            Retry
                          </button>
                        )}
                      </div>
                    </div>
                    {isValidating && (
                      <div className="flex-shrink-0">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-500"></div>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveUrl(url)}
                    disabled={disabled}
                    className={`ml-3 p-1 focus:outline-none focus:ring-2 focus:ring-red-500 rounded disabled:opacity-50 transition-colors ${theme === 'dark'
                      ? 'text-red-400 hover:text-red-300'
                      : 'text-red-600 hover:text-red-500'
                      }`}
                    title="Remove URL"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {urls.length === 0 && (
        <div className={`text-center py-6 border-2 border-dashed rounded-lg ${theme === 'dark'
          ? 'border-gray-600'
          : 'border-gray-300'
          }`}>
          <svg className={`mx-auto h-8 w-8 mb-2 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
            No website sources added yet. Add URLs above to enable web-based knowledge extraction.
          </p>
        </div>
      )}
    </div>
  );
};

export default WebsiteURLManager;
